// @ts-nocheck
/**
 * HistoryDetailView — Salesforce-style Credit Block History record detail.
 *
 * Sections: Details, Date Information, System Information.
 */
import React, { useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Edit3,
} from 'lucide-react';
import type { CreditBlockHistoryEntry } from '../../types';
import { getBureauLogo } from '../../constants/bureauLogos';
import { formatAuditDateTime, formatAuditUserDate } from '../../utils/creditBlockFormatters';

interface HistoryDetailViewProps {
  entry: CreditBlockHistoryEntry;
  clientName: string;
  onBack: () => void;
}

/* ---- Collapsible section ---- */

const SfSection: React.FC<{
  title: string;
  defaultCollapsed?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultCollapsed = false, children }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 text-left hover:bg-gray-150 dark:hover:bg-gray-700/80 transition-colors"
      >
        {collapsed
          ? <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
          : <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />}
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
      </button>
      {!collapsed && <div className="px-4 py-4">{children}</div>}
      {!collapsed && <div className="h-1 bg-blue-500" />}
    </div>
  );
};

/* ---- Read-only field ---- */

const SfField: React.FC<{
  label: string;
  value: React.ReactNode;
  isLink?: boolean;
  showEdit?: boolean;
}> = ({ label, value, isLink = false, showEdit = true }) => {
  const empty = <span className="text-gray-400 dark:text-gray-500 italic">—</span>;
  const display = value == null || value === '' ? empty : value;
  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      <div className="group flex items-center gap-2 min-h-[1.75rem]">
        <div className={`text-sm flex-1 ${isLink ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'}`}>
          {display}
        </div>
        {showEdit && (
          <button
            type="button"
            className="p-0.5 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Edit"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

/* ---- Date formatter ---- */

function fmtDate(v: unknown): string {
  if (!v) return '';
  const s = String(v);
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

/** Pull YYYY-MM-DD from reason/note text (toolbar flows store dates in Reason__c). */
function isoDateFromReason(text: string, which: 'start' | 'end'): string {
  const s = String(text ?? '');
  if (!s) return '';
  if (which === 'end') {
    const fromLabel = s.match(/\bEnd\s*date:\s*(\d{4}-\d{2}-\d{2})\b/i);
    if (fromLabel?.[1]) return fromLabel[1];
    const ends = [...s.matchAll(/\bEnd:\s*(\d{4}-\d{2}-\d{2})\b/gi)];
    if (ends.length) return ends[ends.length - 1][1];
    return '';
  }
  const fromLabel = s.match(/\bStart\s*date:\s*(\d{4}-\d{2}-\d{2})\b/i);
  if (fromLabel?.[1]) return fromLabel[1];
  const fromShort = s.match(/\bStart:\s*(\d{4}-\d{2}-\d{2})\b/i);
  return fromShort?.[1] ?? '';
}

function requestTypeLabel(t: string): string {
  switch (t) {
    case 'lock_request': return 'Lock';
    case 'unlock_request': return 'Unblock';
    case 'permanent_block': return 'Permanent Block';
    case 'permanent_unblock': return 'Permanent Unblock';
    case 'extension': return 'Extension';
    case 'special_case': return 'Special Case';
    default: return t;
  }
}

function bureauLabel(raw: string): string {
  const v = String(raw ?? '').trim().toLowerCase();
  if (!v) return '';
  if (v.includes('equifax')) return 'Equifax';
  if (v.includes('experian')) return 'Experian';
  if (v.includes('transunion') || v.includes('trans union')) return 'TransUnion';
  if (v.includes('chex')) return 'ChexSystems';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function bureauLogo(raw: string): string | undefined {
  const v = String(raw ?? '').trim().toLowerCase();
  if (!v) return undefined;
  if (v.includes('equifax')) return getBureauLogo('equifax') || undefined;
  if (v.includes('experian')) return getBureauLogo('experian') || undefined;
  if (v.includes('transunion') || v.includes('trans union')) return getBureauLogo('transunion') || undefined;
  if (v.includes('chex')) return getBureauLogo('chexsystems') || undefined;
  return undefined;
}

/* ---- Main component ---- */

const HistoryDetailView: React.FC<HistoryDetailViewProps> = ({
  entry,
  clientName,
  onBack,
}) => {
  // Filter out sentinel/placeholder values that shouldn't surface to users (e.g. "-1" from upstream).
  const cleanValue = (v: unknown): string => {
    const s = String(v ?? '').trim();
    if (!s || s === '-1' || s === '0' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return '';
    return s;
  };

  const e = entry;
  const bureauId = cleanValue(e.bureauId ?? e.Bureau__c ?? e.bureau);
  const bureauName = cleanValue(e.bureau ?? e.Bureau_Name__c ?? e.bureauName);
  const bureauIdLabel = bureauLabel(bureauId) || bureauId;
  const bureauNameLabel = bureauLabel(bureauName) || bureauName;
  const bureauLogoSrc = bureauLogo(bureauName) || bureauLogo(bureauId);
  // recordCreated is a date — was incorrectly sourcing from requestedBy (a person).
  const recordCreated = cleanValue(e.Record_Created__c ?? e.requestDate ?? e.CreatedDate__c ?? e.createdDate);
  const reqType = requestTypeLabel(e.type);
  const note = cleanValue(e.description ?? e.Note__c ?? e.note);
  const unlockStartRaw =
    cleanValue(e.startDate) ||
    isoDateFromReason(note, 'start') ||
    cleanValue((e as { Start_Date__c?: string }).Start_Date__c);
  const unlockEndRaw =
    cleanValue(e.endDate) ||
    isoDateFromReason(note, 'end') ||
    cleanValue((e as { End_Date__c?: string }).End_Date__c);
  const createdBy = cleanValue(e.requestedBy ?? e.CreatedBy__c ?? e.createdBy);
  const createdDate = cleanValue(e.requestDate ?? e.CreatedDate__c ?? e.createdDate);
  const lastModifiedBy = cleanValue(e.processedBy ?? e.LastModifiedBy__c ?? e.lastModifiedBy);
  const lastModifiedDate = cleanValue(e.processedDate ?? e.LastModifiedDate__c ?? e.lastModifiedDate);

  return (
    <div className="space-y-4">
      {/* Back */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* Header */}
      <div className="bg-blue-600 rounded-lg px-5 py-4 text-white">
        <p className="text-xs opacity-80">Credit Block History</p>
        <h2 className="text-lg font-bold">{e.id}</h2>
      </div>

      {/* Details tab label */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-1">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 border-b-2 border-blue-600 dark:border-blue-400 pb-2 px-1">
          Details
        </span>
      </div>

      {/* Details section */}
      <SfSection title="Details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <SfField label="Credit Block Client" value={clientName} isLink showEdit={false} />
          <SfField label="Record Created" value={recordCreated ? formatAuditDateTime(recordCreated) : undefined} />
          <SfField
            label="Bureau"
            value={
              bureauIdLabel
                ? (
                  <span className="inline-flex items-center gap-2">
                    {bureauLogoSrc ? <img src={bureauLogoSrc} alt="" className="h-4 w-4 object-contain" aria-hidden /> : null}
                    <span>{bureauIdLabel}</span>
                  </span>
                )
                : undefined
            }
            isLink
          />
          <SfField label="Request Type" value={reqType} />
          <SfField
            label="Bureau Name"
            value={
              bureauNameLabel
                ? (
                  <span className="inline-flex items-center gap-2">
                    {bureauLogoSrc ? <img src={bureauLogoSrc} alt="" className="h-4 w-4 object-contain" aria-hidden /> : null}
                    <span>{bureauNameLabel}</span>
                  </span>
                )
                : undefined
            }
          />
          <SfField label="Note" value={note || undefined} />
        </div>
      </SfSection>

      {/* Date Information */}
      <SfSection title="Date Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <SfField
            label="Start Date"
            value={unlockStartRaw ? fmtDate(unlockStartRaw) : undefined}
          />
          <SfField
            label="End Date"
            value={unlockEndRaw ? fmtDate(unlockEndRaw) : undefined}
          />
        </div>
      </SfSection>

      {/* System Information */}
      <SfSection title="System Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <SfField
            label="Created By"
            value={formatAuditUserDate(createdBy, createdDate) || undefined}
            showEdit={false}
          />
          <SfField
            label="Last Modified By"
            value={formatAuditUserDate(lastModifiedBy, lastModifiedDate) || undefined}
            showEdit={false}
          />
          <SfField
            label="Created Date"
            value={createdDate ? formatAuditDateTime(createdDate) : undefined}
            showEdit={false}
          />
          <SfField
            label="Last Modified Date"
            value={lastModifiedDate ? formatAuditDateTime(lastModifiedDate) : undefined}
            showEdit={false}
          />
        </div>
      </SfSection>
    </div>
  );
};

export default HistoryDetailView;
